import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, ArrowsClockwise, Buildings, Clock } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatMoney, formatDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import CopyCodeButton from "../components/CopyCodeButton";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const REASON_BADGE = {
    price_revision: "bg-amber-50 text-amber-800 border-amber-200",
    qty_return: "bg-rose-50 text-rose-800 border-rose-200",
    deficiency_in_service: "bg-rose-50 text-rose-800 border-rose-200",
    rate_mismatch: "bg-indigo-50 text-indigo-800 border-indigo-200",
    post_sale_discount: "bg-emerald-50 text-emerald-800 border-emerald-200",
    other: "bg-slate-100 text-slate-700 border-slate-300",
};

export default function CreditNotes() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/billing/credit-note/list");
                setItems(data.items || []);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load credit notes"));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        if (!q.trim()) return items;
        const n = q.trim().toLowerCase();
        return items.filter((c) =>
            c.cn_number.toLowerCase().includes(n)
            || (c.invoice_number || "").toLowerCase().includes(n)
            || (c.client?.name || "").toLowerCase().includes(n)
            || (c.reason_label || "").toLowerCase().includes(n)
        );
    }, [items, q]);

    const total = items.reduce((s, c) => s + (c.total_amount || 0), 0);

    return (
        <div data-testid="credit-notes-page">
            <PageHeader
                eyebrow="Commercial corrections"
                title="Credit Notes"
                description="GST-compliant credit notes issued against immutable invoices. Each CN reduces the outstanding balance of its source invoice."
                actions={
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <div className="reedos-eyebrow">Total credited</div>
                            <div className="font-display font-bold text-slate-900 reedos-data text-lg">{formatINR(total)}</div>
                        </div>
                    </div>
                }
            />
            <div className="px-6 sm:px-8 py-8 space-y-5">
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by CN number, invoice, client or reason…"
                    className="max-w-md"
                    data-testid="cn-search-input"
                />
                {loading ? (
                    <div className="text-sm text-slate-500">Loading credit notes…</div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={<ArrowsClockwise size={40} weight="thin" />}
                        title={items.length === 0 ? "No credit notes issued yet" : "No results"}
                        description={
                            items.length === 0
                                ? "Open an invoice with outstanding balance and click “Issue credit note” to create one."
                                : "Try a different search term."
                        }
                    />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="cn-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">CN number</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Date</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Client</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Against invoice</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Reason</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c) => (
                                    <tr key={c.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`cn-row-${c.id}`}>
                                        <td className="px-5 py-3">
                                            <span className="inline-flex items-center">
                                                <Link to={`/credit-notes/${c.id}`} className="font-mono text-sm font-bold text-slate-900 hover:underline">{c.cn_number}</Link>
                                                <CopyCodeButton
                                                    code={c.cn_number}
                                                    label="credit note number"
                                                    testid={`cn-number-copy-${c.id}`}
                                                />
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-xs text-slate-600 inline-flex items-center gap-1.5"><Clock size={12} /> {formatDate(c.cn_date)}</td>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-1.5 text-slate-800">
                                                <Buildings size={12} className="text-slate-400" />
                                                <span className="text-sm">{c.client?.name || "—"}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3">
                                            {c.invoice ? (
                                                <Link to={`/invoices/${c.invoice.id}`} className="font-mono text-xs text-slate-700 hover:underline inline-flex items-center gap-1">
                                                    <Receipt size={11} /> {c.invoice.invoice_number}
                                                </Link>
                                            ) : (
                                                <span className="text-xs text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            <span className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold border ${REASON_BADGE[c.reason_code] || REASON_BADGE.other}`}>
                                                {c.reason_label}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-right reedos-data font-semibold">{formatMoney(c.total_amount, c.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
